/**
 * Support ticket API client — thin wrapper around fetch with admin token.
 *
 * All admin support endpoints are routed through this module so there's a
 * single place to swap the auth header, base URL, or retry logic.
 */

import { storage } from '../../../services/storage';
import { API_BASE_URL } from '../../../utils/api';
import type { TicketStatus, TicketPriority, TicketView } from './statuses';

// ── Types ────────────────────────────────────────────────────────────────────

export interface TicketListItem {
  id: string;
  ticket_number: number;
  source: 'web' | 'app' | 'email';
  subject: string | null;
  category: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  tags: string[];
  requester_email: string | null;
  requester_name: string | null;
  requester_user_id: string | null;
  assigned_admin: { id: string; name: string | null } | null;
  message_count: number;
  last_message_preview: string;
  // Set when this ticket has been promoted (or linked) to a roadmap
  // item. Drives the "Promoted" chip in the inbox row.
  roadmap_item_id: string | null;
  first_response_at: string | null;
  last_requester_message_at: string | null;
  last_agent_message_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TicketMessage {
  id: string;
  direction: 'inbound' | 'outbound' | 'internal_note' | 'system' | 'agent_draft';
  author_type: 'requester' | 'agent' | 'agent_bot' | 'system';
  author_admin_id: string | null;
  author_name: string | null;
  author_email: string | null;
  body_text: string;
  body_html: string | null;
  attachments: Array<Record<string, unknown>>;
  email_delivered: boolean | null;
  email_delivery_error: string | null;
  created_at: string;
}

export interface TicketEvent {
  id: string;
  event_type: string;
  admin_id: string | null;
  admin_name: string | null;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  note: string | null;
  created_at: string;
}

export interface TicketDetail {
  id: string;
  ticket_number: number;
  source: 'web' | 'app' | 'email';
  subject: string | null;
  category: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  tags: string[];
  thread_token: string;
  assigned_admin: { id: string; name: string | null; email: string | null } | null;
  requester: {
    email: string | null;
    name: string | null;
    user_id: string | null;
    linked_user_profile: {
      id: string;
      email: string | null;
      first_name: string | null;
      last_name: string | null;
      status: string | null;
      created_at: string | null;
    } | null;
  };
  first_response_at: string | null;
  last_requester_message_at: string | null;
  last_agent_message_at: string | null;
  solved_at: string | null;
  error_context: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  agent_classification: Record<string, unknown> | null;
  agent_triage_status: string | null;
  linked_artifacts: Array<Record<string, unknown>>;
  created_at: string;
  updated_at: string;
  messages: TicketMessage[];
  events: TicketEvent[];
}

export interface TicketStats {
  new_count: number;
  total: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  by_source: Record<string, number>;
  open_count: number;
  pending_count: number;
  high_urgent_count: number;
  unassigned_count: number;
}

export interface Assignee {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

export interface ListTicketsParams {
  view?: TicketView;
  status?: TicketStatus;
  priority?: TicketPriority;
  source?: 'web' | 'app' | 'email';
  assigned_admin_id?: string;
  unassigned?: boolean;
  tag?: string;
  search?: string;
  promoted?: 'promoted' | 'unpromoted';
  limit?: number;
  offset?: number;
}

export interface TicketUpdatePayload {
  status?: TicketStatus;
  priority?: TicketPriority;
  assigned_admin_id?: string | null;
  clear_assignee?: boolean;
  tags?: string[];
  subject?: string;
  category?: string;
}

// ── Fetch helper ─────────────────────────────────────────────────────────────

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = await storage.getItem('admin_token');
  const base = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? ''
    : API_BASE_URL;
  const res = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || body.message || detail;
    } catch {
      // fall through
    }
    throw new Error(detail);
  }
  return res;
}

// ── API functions ────────────────────────────────────────────────────────────

export async function fetchStats(): Promise<TicketStats> {
  const res = await apiFetch('/api/admin/support/stats');
  return res.json();
}

export async function fetchTickets(
  params: ListTicketsParams = {}
): Promise<{ tickets: TicketListItem[]; total: number; limit: number; offset: number }> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      qs.set(k, String(v));
    }
  });
  const res = await apiFetch(`/api/admin/support/tickets?${qs}`);
  return res.json();
}

export async function fetchTicketDetail(id: string): Promise<TicketDetail> {
  const res = await apiFetch(`/api/admin/support/tickets/${id}`);
  return res.json();
}

export async function updateTicket(id: string, payload: TicketUpdatePayload): Promise<{ success: boolean; changed: boolean }> {
  const res = await apiFetch(`/api/admin/support/tickets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function sendReply(id: string, body: string): Promise<{ success: boolean; message_id: string; new_status: string }> {
  const res = await apiFetch(`/api/admin/support/tickets/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
  return res.json();
}

export async function addNote(id: string, body: string): Promise<{ success: boolean; message_id: string }> {
  const res = await apiFetch(`/api/admin/support/tickets/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
  return res.json();
}

export async function fetchAssignees(): Promise<Assignee[]> {
  const res = await apiFetch('/api/admin/support/assignees');
  const data = await res.json();
  return data.assignees || [];
}

export async function exportCsv(filter?: { status?: string; source?: string }): Promise<Blob> {
  const qs = new URLSearchParams();
  if (filter?.status) qs.set('status', filter.status);
  if (filter?.source) qs.set('source', filter.source);
  const token = await storage.getItem('admin_token');
  const base = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? ''
    : API_BASE_URL;
  const res = await fetch(`${base}/api/admin/support/export?${qs}`, {
    headers: { 'Authorization': `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
}
